import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getAccessToken } from './authToken';

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const SKIP_EVENTS = new Set(['connected', 'timeout']);

/**
 * Parses a single SSE message block into { eventType, data }.
 * Returns null if the message has no data or should be skipped.
 */
function parseSseMessage(message) {
	if (!message.trim()) return null;

	let eventType = 'message';
	let data = null;

	for (const line of message.split('\n')) {
		if (line.startsWith('event: ')) {
			eventType = line.slice(7);
		} else if (line.startsWith('data: ')) {
			try {
				data = JSON.parse(line.slice(6));
			} catch {
				data = line.slice(6);
			}
		}
	}

	if (data === null || SKIP_EVENTS.has(eventType)) return null;
	return { eventType, data };
}

/**
 * Processes a chunk of SSE data: splits into messages, parses each,
 * and dispatches callbacks / query invalidations.
 *
 * @returns {string} remaining buffer (incomplete message)
 */
function processSseChunk(buffer, { onEvent, invalidateKeys, queryClient }) {
	const messages = buffer.split('\n\n');
	const remaining = messages.pop();

	for (const raw of messages) {
		const parsed = parseSseMessage(raw);
		if (!parsed) continue;

		if (onEvent) onEvent(parsed.eventType, parsed.data);

		if (invalidateKeys) {
			for (const key of invalidateKeys) {
				queryClient.invalidateQueries({ queryKey: key });
			}
		}
	}

	return remaining;
}

/**
 * Server-Sent Events over fetch() with the Bearer JWT. Native EventSource cannot send an
 * Authorization header, hence the ReadableStream reader.
 *
 * One subscription per `connectionKey`: changing the key (or disabling) ends every attempt of
 * the old subscription — including one still waiting for its token — and nothing an old
 * attempt delivers afterwards reaches the callbacks. A regular end of the response (the server
 * closes streams after ten minutes) reconnects like a failure, with backoff; `resolveUrl` is
 * asked for the URL at every attempt, so a resume cursor can be current.
 *
 * @param {string|null} connectionKey - Identifies the subscription; null keeps it closed
 * @param {object} options
 * @param {boolean} [options.enabled=true]
 * @param {function(): string} options.resolveUrl - Absolute stream URL for the next attempt
 * @param {function} [options.onEvent] - (eventType, data) => void
 * @param {string[][]} [options.invalidateKeys] - React Query keys to invalidate on any event
 */
export function useSseStream(connectionKey, { enabled = true, resolveUrl, onEvent, invalidateKeys } = {}) {
	const queryClient = useQueryClient();

	// Refs keep inline callbacks from tearing the connection down on every render.
	const resolveUrlRef = useRef(resolveUrl);
	const onEventRef = useRef(onEvent);
	const invalidateKeysRef = useRef(invalidateKeys);
	resolveUrlRef.current = resolveUrl;
	onEventRef.current = onEvent;
	invalidateKeysRef.current = invalidateKeys;

	useEffect(() => {
		if (!enabled || !connectionKey) return undefined;

		// Everything below belongs to this subscription; `closed` ends it, the controllers abort
		// whatever is in flight, including an attempt still waiting for the token.
		let closed = false;
		let retryCount = 0;
		let retryTimer = null;
		const controllers = new Set();

		const scheduleReconnect = () => {
			if (closed) return;
			const delay = RECONNECT_DELAYS[Math.min(retryCount, RECONNECT_DELAYS.length - 1)];
			retryCount += 1;
			retryTimer = setTimeout(connect, delay);
		};

		async function connect() {
			if (closed) return;
			const controller = new AbortController();
			controllers.add(controller);
			try {
				const token = await getAccessToken();
				if (closed || controller.signal.aborted) return;
				if (!token) return;

				const response = await fetch(resolveUrlRef.current(), {
					headers: { Authorization: `Bearer ${token}` },
					signal: controller.signal,
				});
				if (!response.ok) throw new Error(`SSE connection failed: ${response.status}`);

				retryCount = 0;
				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = '';

				// eslint-disable-next-line no-constant-condition
				while (true) {
					const { done, value } = await reader.read();
					if (done || closed) break;
					buffer += decoder.decode(value, { stream: true });
					buffer = processSseChunk(buffer, {
						onEvent: (type, data) => {
							if (!closed) onEventRef.current?.(type, data);
						},
						invalidateKeys: closed ? null : invalidateKeysRef.current,
						queryClient,
					});
				}
				// The server ended the response on purpose (timeout) or dropped it: resume.
				scheduleReconnect();
			} catch (err) {
				if (err.name !== 'AbortError') scheduleReconnect();
			} finally {
				controllers.delete(controller);
			}
		}

		connect();

		return () => {
			closed = true;
			if (retryTimer) clearTimeout(retryTimer);
			for (const controller of controllers) controller.abort();
			controllers.clear();
		};
	}, [enabled, connectionKey, queryClient]);
}
